import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, CheckCircle2, Clock, Loader2, MapPin, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n";

export default function Cancel() {
  const { token } = useParams();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/cancellations/${token}`).then((r) => setInfo(r.data)).catch(() => setError(true));
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/cancellations/${token}`);
      setDone(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-sand" data-testid="cancel-page">
      <Navbar />
      <main className="mx-auto flex w-full max-w-xl flex-1 items-center px-4 pb-24 pt-32 sm:px-6">
        {!info && !error && (
          <div className="flex w-full justify-center"><Loader2 size={32} className="animate-spin text-azure" /></div>
        )}

        {error && (
          <div className="w-full rounded-3xl bg-white p-12 text-center shadow-xl" data-testid="cancel-error">
            <h1 className="font-serif text-2xl font-semibold text-navy">{t("confirmation.notfound_title")}</h1>
            <p className="mt-3 text-sm text-navy/60">{t("confirmation.notfound_text")}</p>
          </div>
        )}

        {done && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full rounded-3xl border border-slate-100 bg-white p-12 text-center shadow-xl" data-testid="cancel-success">
            <CheckCircle2 size={52} className="mx-auto text-emerald-500" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">{t("cancel.done_title")}</h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-navy/60">{t("cancel.done_text")}</p>
            {done.refunded && (
              <p className="mx-auto mt-4 max-w-sm rounded-2xl bg-azure-soft p-4 text-sm font-medium text-navy/70" data-testid="cancel-refund-note">
                {t("cancel.refund", { price: info.price_eur })}
              </p>
            )}
            <Link to="/" className="mt-8 inline-block rounded-full bg-azure px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-azure-hover">
              {t("common.back_home")}
            </Link>
          </motion.div>
        )}

        {info && !done && !error && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="w-full overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl" data-testid="cancel-card">
            <div className="bg-navy px-8 py-8 text-center">
              <XCircle size={40} className="mx-auto text-amber-400" />
              <h1 className="mt-4 font-serif text-2xl font-semibold text-white sm:text-3xl">{t("cancel.title")}</h1>
              <p className="mt-2 text-sm text-white/60">{t("cancel.subtitle")}</p>
            </div>

            <div className="p-8 sm:p-10">
              <dl className="space-y-3.5 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <dt className="flex items-center gap-2 text-navy/50"><MapPin size={15} className="text-azure" /> {t("confirmation.r_pickup")}</dt>
                  <dd className="text-right font-semibold text-navy">{info.pickup_label}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="flex items-center gap-2 text-navy/50"><MapPin size={15} className="text-gold" /> {t("confirmation.r_dropoff")}</dt>
                  <dd className="text-right font-semibold text-navy">{info.dropoff_label}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-navy/50"><Calendar size={15} className="text-azure" /> {t("confirmation.r_date")}</dt>
                  <dd className="font-semibold text-navy">{info.date}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-navy/50"><Clock size={15} className="text-azure" /> {t("confirmation.r_time")}</dt>
                  <dd className="font-semibold text-navy">{info.time}</dd>
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-3.5">
                  <dt className="font-bold text-navy">{t("confirmation.r_fare")}</dt>
                  <dd className="font-serif text-2xl font-semibold text-azure">{info.price_eur} €</dd>
                </div>
              </dl>

              {info.status === "cancelled" && (
                <p className="mt-6 rounded-2xl bg-slate-100 p-4 text-sm text-navy/60" data-testid="cancel-already">{t("cancel.already")}</p>
              )}

              {info.status !== "cancelled" && !info.cancellable && (
                <p className="mt-6 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="cancel-too-late">{t("cancel.too_late")}</p>
              )}

              {info.cancellable && (
                <>
                  <p className="mt-6 flex items-center gap-2 text-sm text-navy/60" data-testid="cancel-hours-left">
                    <ShieldCheck size={15} className="text-emerald-500" /> {t("cancel.hours_left", { hours: info.hours_left })}
                  </p>
                  <button
                    data-testid="cancel-confirm-button"
                    onClick={confirm}
                    disabled={confirming}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-red-500 py-4 text-sm font-bold text-white transition-colors hover:bg-red-600 disabled:opacity-60"
                  >
                    {confirming && <Loader2 size={16} className="animate-spin" />}
                    {t("cancel.confirm")}
                  </button>
                  <p className="mt-3 text-center text-xs text-navy/45">{t("cancel.warning")}</p>
                </>
              )}

              <Link to="/" data-testid="cancel-keep-button"
                className="mt-4 block rounded-full border border-slate-200 py-3.5 text-center text-sm font-bold text-navy transition-colors hover:bg-slate-50">
                {t("cancel.keep")}
              </Link>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
